import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { createTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';

import URLSearchParams from 'url-search-params';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import 'assets/vendors/style';

import {
  AMBER,
  BLUE,
  CYAN,
  DARK_AMBER,
  DARK_BLUE,
  DARK_CYAN,
  DARK_DEEP_ORANGE,
  DARK_DEEP_PURPLE,
  DARK_GREEN,
  DARK_INDIGO,
  DARK_PINK,
  DEEP_ORANGE,
  DEEP_PURPLE,
  GREEN,
  INDIGO,
  PINK,
} from 'constants/ThemeColors';

import RTL from 'util/RTL';
import { isAuthenticated, getToken2FA } from 'util/cobUtils';

import erro404 from 'app/routes/extraPages/routes/404';
import { getControleDeAcesso } from 'util/AuthUtils';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import indigoTheme from './themes/indigoTheme';
import cyanTheme from './themes/cyanTheme';
import orangeTheme from './themes/orangeTheme';
import amberTheme from './themes/amberTheme';
import pinkTheme from './themes/pinkTheme';
import blueTheme from './themes/blueTheme';
import purpleTheme from './themes/purpleTheme';
import greenTheme from './themes/greenTheme';

import AppLocale from '../lngProvider';

import {
  setInitUrl,
  userSignOut,
  getUser,
  showAuthMessage,
} from '../actions/Auth';
import { setDarkTheme, setThemeColor } from '../actions/Setting';

import AppLayout from './AppLayout';
import Code2FA from './Code2FA';
import SignIn from './SignIn';

const ProtectedComponent = ({ component: Component, authUser, ...props }) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!authUser) {
      history.replace('/login', { from: location });
      /*
       <Redirect
          to={{
            pathname: '/login',
            state: { from: props.location },
          }}
        />
        */
    }
  }, [authUser, history, location]);

  return authUser ? <Component {...props} /> : null;
};

const RestrictedRoute = ({ component: Component, authUser, ...rest }) => (
  <Route
    {...rest}
    render={(props) => (
      <ProtectedComponent
        {...props}
        authUser={authUser}
        component={Component}
      />
    )}
  />
);

const App = (props) => {
  const dispatch = useDispatch();

  const { match, location, history } = props;

  const { themeColor, darkTheme, locale } = useSelector(
    ({ settings }) => settings
  );
  const { authUser, authUserInfo, initURL } = useSelector(({ auth }) => auth);
  const authUser2FA = useSelector(({ auth }) => auth.authenticated_2f2);

  const isDarkTheme = darkTheme;

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!authUserInfo) return;

    const { pathname } = props.location;

    const tentandoAcessarProcesso =
      pathname === '/app/cob/processo/' ||
      pathname.startsWith('/app/cob/processo/');

    const podeVisualizar = getControleDeAcesso(
      'cob.processo.visualizar',
      authUserInfo
    );

    if (tentandoAcessarProcesso && !podeVisualizar) {
      props.history.replace('/app/cob/processo-acesso-negado');
    }
  }, [authUserInfo, props.location.pathname]);

  const horariosTimeoutsRef = useRef([]);

  useEffect(() => {
    const checkIfAppAlreadyOpen = () => {
      const tabId = uuidv4();

      const storedTabId = localStorage.getItem('tabId');

      if (!storedTabId) {
        localStorage.setItem('tabId', tabId);
      } else if (storedTabId !== tabId) {
        window.close();
      }

      window.addEventListener('beforeunload', () => {
        localStorage.removeItem('tabId');
      });
    };

    if (authUserInfo?.mono_tab) checkIfAppAlreadyOpen();
  }, [authUserInfo?.mono_tab]);

  useEffect(() => {
    horariosTimeoutsRef.current.forEach(clearTimeout);
    horariosTimeoutsRef.current = [];

    const { pathname } = location;
    const isTelaAutenticacao = pathname === '/login' || pathname === '/twofa';

    if (isTelaAutenticacao || !authUser) {
      return undefined;
    }

    const horarios = authUserInfo?.horarios;
    if (!horarios?.ativo) {
      return undefined;
    }

    const { intervaloAtual } = horarios;
    if (!intervaloAtual) {
      dispatch(showAuthMessage('Fora do horário de acesso'));
      dispatch(userSignOut());
      return undefined;
    }

    const horaFimStr =
      intervaloAtual.horaHf || intervaloAtual.horaFim || horarios.horaHf;

    if (!horaFimStr) {
      return undefined;
    }

    const parseHoraNoDiaAtual = (timeString) => {
      const partes = String(timeString).trim().split(':');
      const horas = parseInt(partes[0], 10);
      const minutos = parseInt(partes[1], 10);
      const segundos = parseInt(partes[2], 10) || 0;

      if (Number.isNaN(horas) || Number.isNaN(minutos)) {
        return null;
      }

      const data = new Date();
      data.setHours(horas, minutos, segundos, 0);
      return data;
    };

    const horaFimDate = parseHoraNoDiaAtual(horaFimStr);
    if (!horaFimDate || Number.isNaN(horaFimDate.getTime())) {
      return undefined;
    }

    const agendar = (delayMs, message, duration, action = () => {}) => {
      if (delayMs <= 0) {
        return;
      }
      const timeoutId = setTimeout(() => {
        enqueueSnackbar(message, {
          variant: 'warning',
          autoHideDuration: duration,
        });
        action();
      }, delayMs);
      horariosTimeoutsRef.current.push(timeoutId);
    };

    const milissegundosAteFim = () => horaFimDate.getTime() - Date.now();

    const agendarAvisosSessao = () => {
      const restanteMs = milissegundosAteFim();

      if (restanteMs <= 0) {
        dispatch(userSignOut());
        return;
      }

      const avisos = [
        {
          offset: 5 * 60 * 1000,
          message: 'Atenção, sua sessão encerra em 5 minutos',
        },
        {
          offset: 3 * 60 * 1000,
          message: 'Atenção, sua sessão encerra em 3 minutos',
        },
        {
          offset: 1 * 60 * 1000,
          message: 'Atenção, sua sessão encerra em 1 minuto',
        },
        {
          offset: 10 * 1000,
          message: 'Atenção, sua sessão encerra em 10 segundos',
        },
        {
          offset: 9 * 1000,
          message: 'Atenção, sua sessão encerra em 9 segundos',
        },
        {
          offset: 8 * 1000,
          message: 'Atenção, sua sessão encerra em 8 segundos',
        },
        {
          offset: 7 * 1000,
          message: 'Atenção, sua sessão encerra em 7 segundos',
        },
        {
          offset: 6 * 1000,
          message: 'Atenção, sua sessão encerra em 6 segundos',
        },
        {
          offset: 5 * 1000,
          message: 'Atenção, sua sessão encerra em 5 segundos',
        },
        {
          offset: 4 * 1000,
          message: 'Atenção, sua sessão encerra em 4 segundos',
        },
        {
          offset: 3 * 1000,
          message: 'Atenção, sua sessão encerra em 3 segundos',
        },
        {
          offset: 2 * 1000,
          message: 'Atenção, sua sessão encerra em 2 segundos',
        },
        {
          offset: 1 * 1000,
          message: 'Atenção, sua sessão encerra em 1 segundo',
        },
        {
          offset: 0,
          message: 'Sessão Encerrada!',
          action: () => dispatch(userSignOut()),
        },
      ];

      avisos.forEach((aviso) => {
        agendar(
          restanteMs - aviso.offset,
          aviso.message,
          aviso.offset <= 10 * 1000 ? 1000 : 5000,
          aviso.action || (() => {})
        );
      });
    };

    agendarAvisosSessao();

    return () => {
      horariosTimeoutsRef.current.forEach(clearTimeout);
      horariosTimeoutsRef.current = [];
    };
  }, [authUser, authUserInfo, location.pathname, dispatch, enqueueSnackbar]);

  useEffect(() => {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    if (initURL === '') {
      dispatch(setInitUrl(props.history.location.pathname));
    }

    const params = new URLSearchParams(props.location.search);
    if (params.has('theme-name')) {
      dispatch(setThemeColor(params.get('theme-name')));
    }

    if (params.has('dark-theme')) {
      dispatch(setDarkTheme());
    }
  }, [
    dispatch,
    initURL,
    props.history.location.pathname,
    props.location.search,
  ]);

  useEffect(() => {
    if (props.history.location.pathname !== '/login') {
      if (isAuthenticated() == false) {
        dispatch(userSignOut());
      }

      // recarrega no redux as informações do usuario
      if (authUserInfo == null) {
        dispatch(getUser());
      }
    }
  }, [props.history.location.pathname]);

  const getColorTheme = (themeColor2, applyTheme) => {
    switch (themeColor2) {
      case INDIGO: {
        applyTheme = createTheme(indigoTheme);
        break;
      }
      case CYAN: {
        applyTheme = createTheme(cyanTheme);
        break;
      }
      case AMBER: {
        applyTheme = createTheme(amberTheme);
        break;
      }
      case DEEP_ORANGE: {
        applyTheme = createTheme(orangeTheme);
        break;
      }
      case PINK: {
        applyTheme = createTheme(pinkTheme);
        break;
      }
      case BLUE: {
        applyTheme = createTheme(blueTheme);
        break;
      }
      case DEEP_PURPLE: {
        applyTheme = createTheme(purpleTheme);
        break;
      }
      case GREEN: {
        applyTheme = createTheme(greenTheme);
        break;
      }
      case DARK_INDIGO: {
        // applyTheme = createTheme({ ...indigoTheme, direction: 'rtl' });
        applyTheme = createTheme(indigoTheme);
        break;
      }
      case DARK_CYAN: {
        applyTheme = createTheme(cyanTheme);
        break;
      }
      case DARK_AMBER: {
        applyTheme = createTheme(amberTheme);
        break;
      }
      case DARK_DEEP_ORANGE: {
        applyTheme = createTheme(orangeTheme);
        break;
      }
      case DARK_PINK: {
        applyTheme = createTheme(pinkTheme);
        break;
      }
      case DARK_BLUE: {
        applyTheme = createTheme(blueTheme);
        break;
      }
      case DARK_DEEP_PURPLE: {
        applyTheme = createTheme(purpleTheme);
        break;
      }
      case DARK_GREEN: {
        applyTheme = createTheme(greenTheme);
        break;
      }
      default:
        createTheme(blueTheme);
    }
    return applyTheme;
  };

  /*
  if (location.pathname === '/test') {
    console.log('test');
    return <Redirect to="/test" />;
  }

  if (location.pathname === '/') {
    if (authUser === null) {
      return <Redirect to="/login" />;
    }

    if (initURL === '' || initURL === '/' || initURL === '/login') {
      return <Redirect to="/app/cob" />;
    }

    return <Redirect to={initURL} />;
  }

  if (
    location.pathname === '/login' &&
    (isAuthenticated() == true || getToken2FA(authUser2FA))
  ) {
    return <Redirect to="/twofa" />;
  }

  if (location.pathname === '/twofa' && isAuthenticated() == true) {
    return <Redirect to="/app/cob" />;
  }
  */

  useLayoutEffect(() => {
    let shouldNavigate = false;
    let newPath = '';

    const path = location.pathname;

    if (path === '/') {
      if (authUser === null) {
        newPath = '/login';
        shouldNavigate = true;
      } else if (initURL === '' || initURL === '/' || initURL === '/login') {
        newPath = '/app/cob';
        shouldNavigate = true;
      } else {
        newPath = initURL;
        shouldNavigate = true;
      }
    } else if (
      path === '/login' &&
      (isAuthenticated() === true || getToken2FA(authUser2FA))
    ) {
      newPath = '/twofa';
      shouldNavigate = true;
    } else if (path === '/twofa' && isAuthenticated() === true) {
      newPath = '/app/cob';
      shouldNavigate = true;
    }

    if (shouldNavigate && newPath !== path) {
      history.replace(newPath);
    }
  }, [location.pathname, authUser, authUser2FA, initURL]);

  let applyTheme = createTheme(blueTheme);
  if (isDarkTheme) {
    document.body.classList.add('dark-theme');
    applyTheme = createTheme(darkTheme);
  } else {
    applyTheme = getColorTheme(themeColor, applyTheme);
  }

  const currentAppLocale = AppLocale[locale.locale];
  return (
    <ThemeProvider theme={applyTheme}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}
        >
          <RTL>
            <div className="app-main">
              <Switch>
                <RestrictedRoute
                  path={`${match.url}app`}
                  authUser={authUser}
                  component={AppLayout}
                  history={history}
                />
                <Route path="/login" component={SignIn} />
                <Route path="/twofa" component={Code2FA} />
                <Route component={erro404} />
              </Switch>
            </div>
          </RTL>
        </IntlProvider>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  );
};

// Se você está lendo isso, parabéns: você faz parte da próxima geração de devs da Cubo.
// Eu escrevi algumas dessas linhas com café, raiva e paixão.
// Faça melhor do que eu. Quebre o código, mas nunca o espírito.
// Faça por prazer e nunca por obrigação.	pois a obrigação rouba o prazer e o prazer te faz te faz esquecer o tempo enquanto o código cria vida.

export default function IntegrationNotistack_App(props) {
  return (
    <SnackbarProvider
      maxSnack={5}
      autoHideDuration={3000}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <App {...props} />
    </SnackbarProvider>
  );
}
